import { useSyncExternalStore, type ReactElement } from "react";
import { COIN_ICON_KEYS, CURRENCY_COIN, CURRENCY_ICON_KEYS, STANDIN_CURRENCIES, allCoinParts, coinCount, type CoinPart, type StandinCurrency } from "odyssey-shared";
import { ArtIcon, type ArtBox } from "../../ui/kit/ArtIcon.tsx";
import { Glyph, type GlyphName } from "../../ui/kit/Glyph.tsx";
import { GLYPH_SIZES, centreOffset, logical } from "../../ui/kit/metrics.ts";
import { iconArt } from "../icons/art.ts";
import { useIcons, type IconsView } from "../icons/context.tsx";
import type { TipController } from "../bar/tips.ts";
import { useTipHandlers } from "../bar/tip-handlers.ts";
import { COINS_GROUP_NAME, COIN_COLOURS, coinPartName, standinName, walletTipAnchor } from "./describe.ts";
import type { WalletSource } from "./store.ts";
import { STANDIN_COUNT, WALLET_GEOMETRY } from "./width.ts";

/**
 * The wallet line (P2.8-T38, P2.8-T59 and P2.8-T67, ADR-124 decisions 5.3, 5.5 and 6 and its notes for P2.8-T59 and P2.8-T67; the design's
 * `CurrencyMark`s in the Inventory card's footer, a visual reference): Valish coin as all three of its parts,
 * Sovereigns, Talons and Bits, each its coin's art in its metal's colour then its number, a zero part's number muted; then Honor, Marks of the
 * Lyceum and Inscription Tokens as labelled stand-ins, each its art then 0, dimmed, since no system earns them yet, or
 * its art alone on a footer too narrow for the line beside the carry number (P2.8-T53).
 * The art is the icon atlas's rows at box 16 on whole device pixels (ADR-111 decision 12); before the atlas loads,
 * where it cannot, or at a UI scale where box 16 draws nothing, each mark is its kit glyph at 12, centred where the art
 * stands, so the swap moves nothing. Each mark is its own picture, named as its tooltip reads: a coin part "0 Sovereigns",
 * "1 Talon" or "12 Bits", a stand-in "Honor: not earned yet". Each is an anchor of the page's `TipController`
 * (`wallet:<n>`, its place in `WALLET_MARKS`), whose label `HudTooltip` draws, and takes keyboard focus where the page
 * shows tooltips. Nothing is drawn until a `wallet` naming Valish coin has arrived: the line holds its height empty, so
 * the window's body never jumps.
 */

/**
 * The line's geometry in logical pixels: each mark's box, 16, the gap of 2 between a mark and its number, of 4 between
 * the coin parts (ADR-124 decision 5.3) and of 6 between currencies (the design's Inventory card), the same numbers
 * `walletLineWidth` works the line's width out from.
 */
export const WALLET_LINE = WALLET_GEOMETRY satisfies { readonly box: ArtBox; readonly coinGap: number; readonly partGap: number; readonly currencyGap: number };

/**
 * Each stand-in currency's glyph where its art is not drawn, the design's glyph for it, drawn as original pixel art.
 */
const STANDIN_GLYPHS: { readonly [code in StandinCurrency]: GlyphName } = { honor: "trophy", marks: "book", tokens: "script-text" };

/**
 * One mark's picture: its art in the box, or its glyph at 12 centred in the box, in a colour where one is given.
 *
 * @param props The `icons`, the art's `iconKey`, the `thing` a missing key is logged as, the fallback `glyph` and its
 *   `colour` (undefined for the text colour).
 * @returns The picture.
 */
function MarkPicture({ icons, iconKey, thing, glyph, colour }: { readonly icons: IconsView; readonly iconKey: string; readonly thing: string; readonly glyph: GlyphName; readonly colour: string | undefined }): ReactElement {
  const art = iconArt(icons.snapshot, iconKey, thing, WALLET_LINE.box, icons.log);
  if (art !== null) {
    return <ArtIcon art={art} room={WALLET_LINE.box} at={null} />;
  }
  const glyphAt = centreOffset(WALLET_LINE.box, GLYPH_SIZES[glyph]);
  return (
    <span className="ot-wallet__glyph" data-wallet="glyph" style={{ width: logical(WALLET_LINE.box), height: logical(WALLET_LINE.box), paddingLeft: logical(glyphAt), paddingTop: logical(glyphAt), color: colour }}>
      <Glyph name={glyph} />
    </span>
  );
}

/**
 * One coin part: its coin's art or glyph in the box, in its metal's colour whatever the count, then its number, muted
 * when it is zero, named as its tooltip reads.
 *
 * @param props The `part`, its place in `WALLET_MARKS` (`index`), the tree's `icons`, and whether it takes keyboard
 *   focus (`focusable`).
 * @returns The part.
 */
function CoinMark({ part, index, icons, focusable }: { readonly part: CoinPart; readonly index: number; readonly icons: IconsView; readonly focusable: boolean }): ReactElement {
  const zero = part.count === 0;
  return (
    <span className={zero ? "ot-wallet__mark is-dimmed" : "ot-wallet__mark"} data-wallet="coin" data-coin={part.coin} data-zero={zero ? "" : undefined} data-tip={walletTipAnchor(index)} role="img" aria-label={coinPartName(part)} tabIndex={focusable ? 0 : undefined} style={{ columnGap: logical(WALLET_LINE.coinGap) }}>
      <MarkPicture icons={icons} iconKey={COIN_ICON_KEYS[part.coin]} thing={`coin ${part.coin}`} glyph="coin" colour={COIN_COLOURS[part.coin]} />
      <span className="ot-wallet__count" data-wallet="count">
        {coinCount(part)}
      </span>
    </span>
  );
}

/**
 * One stand-in currency: its art or glyph in the box, then 0 unless drawn bare, dimmed, named as not earned yet.
 *
 * @param props The currency's `code`, its place in `WALLET_MARKS` (`index`), the tree's `icons`, whether it takes
 *   keyboard focus (`focusable`), and whether it draws its picture without its 0 (`bare`).
 * @returns The mark.
 */
function StandinMark({ code, index, icons, focusable, bare }: { readonly code: StandinCurrency; readonly index: number; readonly icons: IconsView; readonly focusable: boolean; readonly bare: boolean }): ReactElement {
  return (
    <span className="ot-wallet__mark is-dimmed" data-wallet="currency" data-currency={code} data-bare={bare ? "" : undefined} data-tip={walletTipAnchor(index)} role="img" aria-label={standinName(code)} tabIndex={focusable ? 0 : undefined} style={{ columnGap: logical(WALLET_LINE.coinGap) }}>
      <MarkPicture icons={icons} iconKey={CURRENCY_ICON_KEYS[code]} thing={`currency ${code}`} glyph={STANDIN_GLYPHS[code]} colour={undefined} />
      {bare ? null : (
        <span className="ot-wallet__count" data-wallet="count">
          {STANDIN_COUNT}
        </span>
      )}
    </span>
  );
}

/**
 * The wallet line, for the Inventory window's footer.
 *
 * @param props The wallet's `source`, the page's tooltip controller (`tips`, null or absent where nothing shows
 *   tooltips), which each mark hands its hover, press, focus and click to, and whether the stand-ins draw their
 *   pictures without their 0 (`bare`, on a footer too narrow for the line beside the carry number, P2.8-T53).
 * @returns The line, or its empty place before a `wallet` naming Valish coin has arrived.
 * @example
 * <WalletLine source={wallet} tips={tips} bare={false} />
 */
export function WalletLine({ source, tips = null, bare = false }: { readonly source: WalletSource; readonly tips?: TipController | null; readonly bare?: boolean }): ReactElement {
  const wallet = useSyncExternalStore(source.subscribe, source.getSnapshot, source.getSnapshot);
  const icons = useIcons();
  const handlers = useTipHandlers(tips);
  const focusable = tips !== null;
  const copper = wallet?.[CURRENCY_COIN];
  if (copper === undefined) {
    return <span className="ot-wallet" data-wallet="none" style={{ height: logical(WALLET_LINE.box) }} />;
  }
  const parts = allCoinParts(copper);
  return (
    <span className="ot-wallet" data-wallet="line" {...handlers} style={{ height: logical(WALLET_LINE.box), columnGap: logical(WALLET_LINE.currencyGap) }}>
      <span className="ot-wallet__coins" data-wallet="coins" role="group" aria-label={COINS_GROUP_NAME} style={{ columnGap: logical(WALLET_LINE.partGap) }}>
        {parts.map((part, index) => (
          <CoinMark key={part.coin} part={part} index={index} icons={icons} focusable={focusable} />
        ))}
      </span>
      {STANDIN_CURRENCIES.map((code, index) => (
        <StandinMark key={code} code={code} index={parts.length + index} icons={icons} focusable={focusable} bare={bare} />
      ))}
    </span>
  );
}
